import { renderMetaTags, TitleMetaLinkTag, useQuerySubscription } from "react-datocms";
import classNames from "classnames";
import { GetStaticProps, GetStaticPaths } from "next";
import Image from "next/image";
import { Api } from "@/api";
import { Layout, SEO, Section, Container, ClockIcon, LocationIcon, PeopleIcon } from "@/components";
import { DATOCMS_API_TOKEN, ROUTES } from "@/config";
import { metaTagsFragment, responsiveImageFragment, StructuredText, structuredTextFields } from "@/modules";
import { Subscription, IJob } from "@/types";
import { AnchorButton, HorizontalRule, PromoBanner } from "@narrative-software/narrative-web-ui";
import styles from "../../styles/custom.module.css";

interface IJobPage {
  page: IJob & {
    seo: TitleMetaLinkTag[];
  };
}

type Props = {
  preview: boolean;
  subscription: Subscription<IJobPage>;
};

const JobPage: React.FC<Props> = ({ preview, subscription }) => {
  const { data } = useQuerySubscription(subscription);
  if (!data) return null;

  const { title, location, employeeType, department, link, content, seo } = data.page;

  return (
    <Layout preview={preview}>
      <SEO title={`${title} | Narrative`}>{renderMetaTags(seo)}</SEO>

      <Section isFirstSection>
        <Container>
          <div className="mb-7 lg:mb-14">
            <h1 className="text-h2 font-semibold leading-tight text-center lg:text-h1 lg:mb-10">{title}</h1>
            <HorizontalRule className="hidden lg:block" />
          </div>

          <div className={classNames("grid grid-cols-1 gap-10 lg:gap-22 xl:gap-30", styles.grid)}>
            <div>
              <div className="top-28 border-t-4 border-current lg:sticky">
                <ul className="my-7">
                  <li title="Location" className="flex mb-3">
                    <LocationIcon className="mr-3 flex-shrink-0" /> {location}
                  </li>
                  <li title="Employment type" className="flex mb-3">
                    <ClockIcon className="mr-3 flex-shrink-0" /> {employeeType}
                  </li>
                  <li title="Department" className="flex">
                    <PeopleIcon className="mr-3 flex-shrink-0" /> {department}
                  </li>
                </ul>
                <div className="grid grid-cols-2 gap-3 lg:grid-cols-1">
                  <AnchorButton href={link} colour="black" external>
                    Apply now
                  </AnchorButton>
                  <AnchorButton href={`/${ROUTES.CAREERS.SLUG}`} colour="black" appearance="secondary">
                    Careers at Narrative
                  </AnchorButton>
                </div>
              </div>
            </div>
            <div className="prose text-stealth-bomber">
              <StructuredText data={content} />
            </div>
          </div>
        </Container>
      </Section>

      <PromoBanner
        title="Learn about working at Narrative"
        textColour="black"
        backgroundColour="pikachu"
        image={<Image src="/images/photographer-male-back.svg" alt="" width="220" height="320" unoptimized />}
      >
        <AnchorButton href={`/${ROUTES.CAREERS.SLUG}`} colour="black">
          Careers at Narrative
        </AnchorButton>
      </PromoBanner>
    </Layout>
  );
};

/**
 * Static paths
 */
export const getStaticPaths: GetStaticPaths<{ slug: string }> = async () => {
  const {
    data: {
      data: { pages },
    },
  } = await Api.datocmsGraphqlRequest<{ pages: IJob[] }>({
    query: `
      {
        pages: allPageJobs {
          slug
        }
      }
    `,
  });

  return {
    paths: pages.map(({ slug }) => ({ params: { slug } })),
    fallback: false,
  };
};

/**
 * Static props
 */
export const getStaticProps: GetStaticProps<Props> = async ({ params, preview = false }) => {
  const graphqlRequest = {
    preview,
    variables: {
      slug: params?.slug,
    },
    query: `
      query($slug: String) {
        page: pageJob(filter: { slug: { eq: $slug }}) {
          id
          title
          slug
          location
          employeeType
          department
          link
          content {
            ${structuredTextFields}
          }
          seo: _seoMetaTags {
            ...metaTagsFragment
          }
        }
      }
      ${metaTagsFragment}
      ${responsiveImageFragment}
    `,
  };

  const {
    data: { data: initialData },
  } = await Api.datocmsGraphqlRequest<IJobPage>(graphqlRequest);

  // If the page doesn’t exist, throw 404
  if (!initialData?.page) {
    return { notFound: true };
  }

  return {
    props: {
      preview,
      subscription: preview
        ? {
            ...graphqlRequest,
            initialData,
            token: DATOCMS_API_TOKEN,
          }
        : {
            initialData,
            enabled: false,
          },
    },
  };
};

export default JobPage;
